import { ArrowRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FORMATS_BY_KIND, formatName, OUTPUT_KINDS, type OutputFormatId } from '@/core/project/output-format';
import { cn } from '@/lib/cn';
import { DropTarget } from './IntentBands';

/** Every format, grouped by kind in the order the export dialog shows them. */
const FORMATS: readonly OutputFormatId[] = OUTPUT_KINDS.flatMap((kind) => FORMATS_BY_KIND[kind]);

export interface ConvertPanelProps {
  readonly output: OutputFormatId;
  /** "Keep it under N MB" from the link (`?target=20mb`), or null. */
  readonly sizeLimit: number | null;
  readonly onOutput: (output: OutputFormatId) => void;
  readonly onDropLimit: () => void;
  readonly onFiles: (files: File[]) => void;
  /** Back to the recipes: the visitor wants to edit, not only convert. */
  readonly onEdit: () => void;
  readonly dragging: boolean;
  readonly loading: string | false;
}

/**
 * Quick convert (`/?to=mp4`, the converter pages' button): one panel in place
 * of the recipes, saying what will happen, with the format still changeable.
 */
export function ConvertPanel({ output, sizeLimit, onOutput, onDropLimit, onFiles, onEdit, dragging, loading }: ConvertPanelProps) {
  const { t, i18n } = useTranslation();
  const format = formatName(output);
  const limit = sizeLimit === null ? null : sizeLimit.toLocaleString(i18n.language);
  return (
    <section aria-labelledby="convert-title" className="border-t border-line-strong pt-4">
      <div className="-mx-3 grid gap-x-8 gap-y-5 rounded-[22px] bg-panel p-4 shadow-float ring-1 ring-line sm:-mx-5 sm:p-5 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-center">
        <div className="min-w-0">
          <h2 id="convert-title" className="text-[21px] leading-tight font-semibold tracking-[-0.03em] text-balance text-fg sm:text-[24px]">
            {limit ? t('home.convert.titleLimited', { limit }) : t('home.convert.title', { format })}
          </h2>
          <p className="mt-1.5 max-w-120 text-[15px] leading-snug text-pretty text-fg-muted">
            {limit ? t('home.convert.bodyLimited', { format, limit }) : t('home.convert.body', { format })}
          </p>
          <div role="radiogroup" aria-label={t('home.convert.formats')} className="mt-4 flex flex-wrap gap-1.5">
            {FORMATS.map((id) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={id === output}
                onClick={() => onOutput(id)}
                className={cn(
                  'h-8 min-w-12 rounded-lg px-2.5 text-[13px] font-medium tabular ring-1 transition-colors duration-150 ring-inset outline-none focus-visible:ring-2 focus-visible:ring-accent',
                  id === output ? 'bg-accent-soft text-fg ring-accent-strong/60' : 'bg-elevated text-fg-muted ring-line hover:text-fg',
                )}
              >
                {formatName(id)}
              </button>
            ))}
          </div>
          {limit && (
            <span className="mt-2 inline-flex h-8 items-center gap-1 rounded-lg bg-accent-soft pr-1 pl-2.5 text-[13px] font-medium text-fg tabular ring-1 ring-accent-strong/60 ring-inset">
              {t('home.convert.limit', { limit })}
              <button
                type="button"
                onClick={onDropLimit}
                aria-label={t('home.convert.dropLimit')}
                title={t('home.convert.dropLimit')}
                className="flex size-6 items-center justify-center rounded-md text-fg-muted outline-none hover:bg-panel hover:text-fg focus-visible:ring-2 focus-visible:ring-accent"
              >
                <X size={13} />
              </button>
            </span>
          )}
        </div>
        <DropTarget dragging={dragging} loading={loading} onFiles={onFiles} hint={t('home.convert.hint')} />
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="group mt-4 inline-flex items-center gap-1.5 rounded-md px-2 text-[13px] font-medium text-fg-muted transition-colors duration-200 outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-accent"
      >
        {t('home.convert.edit')}
        <ArrowRight size={14} className="transition-transform duration-200 ease-out-soft group-hover:translate-x-0.5" />
      </button>
    </section>
  );
}
